/* eslint-disable */
import { assetImages } from "constants";
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as url from "helper/UrlHelper";

const LeftMenuPanelPopup = ({ moduleName, userInfo }) => {
  const { t } = useTranslation(); // Translation hook
  const location = useLocation();
  const moduleAccess = localStorage.getItem("moduleaccess") || ""; // Get module access permissions
  const userRole = userInfo?.role?.slug || ""; // Extract user role

  // Construct user image URL with fallback
  const userImage = userInfo?.photoimage
    ? url.SERVER_URL + userInfo.photoimage.path
    : assetImages?.defaultUser;

  // Check if user is admin
  const isAdmin = ["ADMIN", "SUPER_ADMIN"].includes(userRole);

  // Check module access permissions
  const hasModuleAccess = (moduleKey) =>
    isAdmin || moduleAccess.includes(moduleKey);

  /**
   * Menu Configuration Array
   *
   * Structure:
   *   path: Route path (for leaf items)
   *   icon: Material icon name
   *   label: Translation key
   *   module: Module identifier for active state
   *   show: Conditional display (true = always show, false = never show, function = conditional)
   *   children: Array of sub-menu items (makes this item an accordion parent)
   *
   * Add new menu items here following the same structure
   */
  const menuItems = [
    // Always visible items
    {
      path: "/admin/dashboard",
      icon: "grid_view",
      label: "Dashboard",
      module: "dashboard",
    },
    {
      path: "/admin/directaccess",
      icon: "switch_access_2",
      label: "Direct Access",
      module: "directaccess",
    },

    // Conditional flat items
    // Administration accordion group
    {
      icon: "admin_panel_settings",
      label: "Administration",
      module: "administration",
      show: isAdmin,
      children: [
        {
          path: "/admin/administration/ecosystems",
          pathMatch: "/admin/administration/ecosystems",
          icon: "hub",
          label: "Ecosystem",
          module: "administration",
        },
        {
          path: "/admin/administration/roles",
          pathMatch: "/admin/administration/roles",
          icon: "manage_accounts",
          label: "Roles",
          module: "administration",
        },
        {
          path: "/admin/administration/actionlogs",
          pathMatch: "/admin/administration/actionlogs",
          icon: "history",
          label: "Action Logs",
          module: "administration",
        },
        {
          path: "/admin/administration/modules",
          pathMatch: "/admin/administration/modules",
          icon: "extension",
          label: "Modules",
          module: "administration",
        },
        {
          path: "/admin/administration/moduleroleprivilege",
          pathMatch: "/admin/administration/moduleroleprivilege",
          icon: "key",
          label: "Module Privilege by Role",
          module: "administration",
        },
        {
          path: "/admin/administration/categories/list",
          pathMatch: "/admin/administration/categories",
          icon: "category",
          label: "Category",
          module: "administration",
        },
        {
          path: "/admin/administration/plans",
          pathMatch: "/admin/administration/plans",
          icon: "card_membership",
          label: "Plans",
          module: "administration",
        },
        {
          path: "/admin/administration/tags",
          pathMatch: "/admin/administration/tags",
          icon: "sell",
          label: "Admin Tags",
          module: "administration",
        },
        {
          path: "/admin/administration/activityprocess/list",
          pathMatch: "/admin/administration/activityprocess",
          icon: "account_tree",
          label: "Activity Process Template",
          module: "administration",
        },
        {
          path: "/admin/administration/mailtemplate/list",
          pathMatch: "/admin/administration/mailtemplate",
          icon: "mail",
          label: "Mail Templates",
          module: "administration",
        },
        {
          path: "/admin/administration/companytype",
          pathMatch: "/admin/administration/companytype",
          icon: "business",
          label: "Company Type",
          module: "administration",
        },
        {
          path: "/admin/administration/producttype",
          pathMatch: "/admin/administration/producttype",
          icon: "category",
          label: "Product Type",
          module: "administration",
        },
        {
          path: "/admin/administration/eventtype",
          pathMatch: "/admin/administration/eventtype",
          icon: "event_note",
          label: "Event Type",
          module: "administration",
        },
      ],
    },
    {
      path: "/admin/appuser/list",
      icon: "person",
      label: "App Users",
      module: "appuser",
      show: isAdmin,
    },
    // CRM accordion group
    {
      icon: "space_dashboard",
      label: "CRM",
      module: "crm",
      show: hasModuleAccess("MOD_CRM"),
      children: [
        {
          path: "/admin/crm/lead/list",
          pathMatch: "/admin/crm/lead",
          icon: "person_search",
          label: "Leads",
          module: "crm",
        },
        {
          path: "/admin/crm/ecolead/list",
          pathMatch: "/admin/crm/ecolead",
          icon: "eco",
          label: "EcoLeads",
          module: "crm",
        },
        {
          path: "/admin/crm/contact/list",
          pathMatch: "/admin/crm/contact",
          icon: "contacts",
          label: "Contact",
          module: "crm",
        },
        {
          path: "/admin/crm/conversation/recieved",
          pathMatch: "/admin/crm/conversation",
          icon: "forum",
          label: "Conversation",
          module: "crm",
        },
        {
          path: "/admin/crm/list",
          pathMatch: "/admin/crm/list",
          icon: "list_alt",
          label: "List",
          module: "crm",
        },
        {
          path: "/admin/crm/mailtemplate/list",
          pathMatch: "/admin/crm/mailtemplate",
          icon: "mail",
          label: "Mail Templates",
          module: "crm",
        },
        {
          path: "/admin/crm/label/list",
          pathMatch: "/admin/crm/label",
          icon: "label",
          label: "Label",
          module: "crm",
        },
        {
          path: "/admin/crm/process/list",
          pathMatch: "/admin/crm/process",
          icon: "account_tree",
          label: "Activity Process",
          module: "crm",
        },
        {
          path: "/admin/crm/opportunity/list",
          pathMatch: "/admin/crm/opportunity",
          icon: "trending_up",
          label: "Opportunities",
          module: "crm",
        },
        {
          path: "/admin/crm/note/list",
          pathMatch: "/admin/crm/note",
          icon: "sticky_note_2",
          label: "Notes",
          module: "crm",
        },
      ],
    },
    {
      path: "/admin/companies",
      icon: "meeting_room",
      label: "Companies",
      module: "companies",
      show: hasModuleAccess("MOD_COMPANY"),
    },
    {
      path: "/admin/events/list",
      icon: "calendar_today",
      label: "Events",
      module: "events",
      show: hasModuleAccess("MOD_EVENT"),
    },
    {
      path: "/admin/group/list",
      icon: "diversity_2",
      label: "Groups",
      module: "group",
      show: hasModuleAccess("MOD_GROUP"),
    },
    {
      path: "/admin/survey/list",
      icon: "live_help",
      label: "Survey",
      module: "survey",
      show: hasModuleAccess("MOD_SURVEY"),
    },
    // Project Management accordion group
    {
      icon: "contract",
      label: "Project Management",
      module: "projects",
      show: hasModuleAccess("MOD_PROJECT"),
      children: [
        {
          path: "/admin/projectmanagement/dashboard",
          pathMatch: "/admin/projectmanagement/dashboard",
          icon: "grid_view",
          label: "Dashboard",
          module: "projects",
        },
        {
          path: "/admin/projectmanagement/list",
          pathMatch: "/admin/projectmanagement/list",
          icon: "folder_open",
          label: "Projects",
          module: "projects",
        },
        {
          path: "/admin/mytasklist",
          pathMatch: "/admin/mytasklist",
          icon: "checklist",
          label: "Tasks",
          module: "mytask",
          show: hasModuleAccess("MOD_TASK"),
        },
        {
          path: "/admin/alllogs/list",
          pathMatch: "/admin/alllogs/list",
          icon: "timer",
          label: "Logs",
          module: "alllogs",
          show: hasModuleAccess("MOD_LOGS"),
        },
        {
          path: "/admin/workingtasks",
          pathMatch: "/admin/workingtasks",
          icon: "engineering",
          label: "Working Now",
          module: "taskworking",
          show: hasModuleAccess("MOD_TASK"),
        },
      ],
    },

    // Challenges accordion group
    {
      icon: "work",
      label: "Challenges",
      module: "challenges",
      show: hasModuleAccess("MOD_CHALLENGE"),
      children: [
        {
          path: "/admin/challenges/dashboard",
          pathMatch: "/admin/challenges/dashboard",
          icon: "grid_view",
          label: "Dashboard",
          module: "challenges",
        },
        {
          path: "/admin/challenges/list",
          pathMatch: "/admin/challenges/list",
          icon: "emoji_events",
          label: "Challenges",
          module: "challenges",
        },
        {
          path: "/admin/challenges/globalreport",
          pathMatch: "/admin/challenges/globalreport",
          icon: "bar_chart",
          label: "Report",
          module: "challenges",
        },
      ],
    },
    {
      path: "/admin/courses",
      icon: "local_library",
      label: "Courses",
      module: "courses",
      show: hasModuleAccess("MOD_COURSE"),
    },
    // Marketplace accordion group
    {
      icon: "storefront",
      label: "Marketplace",
      module: "marketplace",
      show: hasModuleAccess("MOD_MARKETPLACE"),
      children: [
        {
          path: "#",
          pathMatch: "/admin/marketplace/dashboard",
          icon: "grid_view",
          label: "Dashboard",
          module: "marketplace",
        },
        {
          path: "/admin/marketplace/products/list",
          pathMatch: "/admin/marketplace/products",
          icon: "inventory_2",
          label: "Products",
          module: "marketplace",
        },
        {
          path: "/admin/marketplace/inventory/list",
          pathMatch: "/admin/marketplace/inventory",
          icon: "warehouse",
          label: "Inventory",
          module: "marketplace",
        },
        {
          path: "/admin/marketplace/orders/list",
          pathMatch: "/admin/marketplace/orders",
          icon: "shopping_cart",
          label: "Orders",
          module: "marketplace",
        },
        {
          path: "/admin/marketplace/invoices/list",
          pathMatch: "/admin/marketplace/invoices",
          icon: "receipt_long",
          label: "Invoices",
          module: "marketplace",
        },
        {
          path: "/admin/marketplace/settings",
          pathMatch: "/admin/marketplace/settings",
          icon: "settings",
          label: "Settings",
          module: "marketplace",
        },
        {
          path: "/admin/marketplace/product/customerpricing",
          pathMatch: "/admin/marketplace/product/customerpricing",
          icon: "price_change",
          label: "Customer Pricing",
          module: "marketplace",
        },
      ],
    },
    {
      path: "/admin/blogs/list",
      icon: "article",
      label: "Blog",
      module: "blogs",
      show: hasModuleAccess("MOD_CMS"),
    },
    {
      path: "/admin/websitebuilder/list",
      icon: "web",
      label: "Website Builder",
      module: "websitebuilder",
      show: isAdmin,
    },

    // Finance accordion group
    {
      icon: "payments",
      label: "Finance",
      module: "finance_group",
      show:
        hasModuleAccess("MOD_INVOICE") ||
        hasModuleAccess("MOD_PAYMENT") ||
        hasModuleAccess("MOD_SUBSCRIPTION") ||
        isAdmin,
      children: [
        {
          path: "/admin/invoice/list",
          icon: "point_of_sale",
          label: "Invoices",
          module: "invoice",
          show: hasModuleAccess("MOD_INVOICE"),
        },
        {
          path: "/admin/invoicedoc/list",
          icon: "receipt_long",
          label: "Invoice Docs",
          module: "invoicedoc",
          show: isAdmin,
        },
        {
          path: "/admin/payment/list",
          icon: "euro",
          label: "Payments",
          module: "payment",
          show: hasModuleAccess("MOD_PAYMENT"),
        },
        {
          path: "/admin/subscription/list",
          icon: "card_membership",
          label: "Subscription",
          module: "subscription",
          show: hasModuleAccess("MOD_SUBSCRIPTION"),
        },
      ],
    },

    // Support accordion group
    {
      icon: "support_agent",
      label: "Support",
      module: "support_group",
      show: hasModuleAccess("MOD_TICKET") || hasModuleAccess("MOD_FAQ"),
      children: [
        {
          path: "/admin/tickets/list",
          icon: "transit_ticket",
          label: "Tickets",
          module: "tickets",
          show: hasModuleAccess("MOD_TICKET"),
        },
        {
          path: "/admin/faq/list",
          icon: "quiz",
          label: "FAQ",
          module: "faq",
          show: hasModuleAccess("MOD_FAQ"),
        },
      ],
    },

    // Automation accordion group
    {
      icon: "smart_toy",
      label: "Automation",
      module: "automation",
      show: hasModuleAccess("MOD_AUTOMATION"),
      children: [
        {
          path: "/admin/automation/list",
          pathMatch: "/admin/automation/list",
          icon: "tune",
          label: "Config",
          module: "automation",
        },
        {
          path: "/admin/automation/events/list",
          pathMatch: "/admin/automation/events",
          icon: "bolt",
          label: "Events",
          module: "automation",
        },
        {
          path: "/admin/automation/scheduletemplates",
          pathMatch: "/admin/automation/scheduletemplates",
          icon: "edit_calendar",
          label: "Schedule Template",
          module: "automation",
        },
        {
          path: "/admin/automation/taskschedules",
          pathMatch: "/admin/automation/taskschedules",
          icon: "schedule",
          label: "Schedule Tasks",
          module: "automation",
        },
        {
          path: "/admin/automation/childtasks",
          pathMatch: "/admin/automation/childtasks",
          icon: "account_tree",
          label: "Schedule Child Tasks",
          module: "automation",
        },
      ],
    },
    {
      path: "/admin/media/library",
      icon: "image",
      label: "Media Library",
      module: "media",
      show: true,
    },
    {
      path: "/admin/webhook/list",
      icon: "settings",
      label: "Webhook",
      module: "webhook",
      show: isAdmin,
    },
    {
      path: "/admin/api/docs",
      icon: "code",
      label: "API Documentation",
      module: "api",
      show: hasModuleAccess("MOD_API_DOCS"),
    },
  ];

  // Check if a child item is active (supports both module-based and path-based matching)
  const isChildActive = (child, parentModule) => {
    if (child.pathMatch) {
      return location.pathname.startsWith(child.pathMatch);
    }
    return child.module !== parentModule && child.module === moduleName;
  };

  // Determine which accordion groups should be open based on active module / path
  const getInitialOpenMenus = () => {
    const initial = {};
    menuItems.forEach((item) => {
      if (item.children) {
        const hasActive =
          item.module === moduleName ||
          item.children.some((child) => isChildActive(child, item.module));
        if (hasActive) {
          initial[item.module] = true;
        }
      }
    });
    return initial;
  };

  const [openMenus, setOpenMenus] = useState(getInitialOpenMenus);

  // Re-evaluate open menus when route changes
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.children) {
        const hasActive =
          item.module === moduleName ||
          item.children.some((child) => isChildActive(child, item.module));
        if (hasActive) {
          setOpenMenus((prev) => ({ ...prev, [item.module]: true }));
        }
      }
    });
  }, [moduleName, location.pathname]);

  const toggleMenu = (module) => {
    setOpenMenus((prev) => ({ ...prev, [module]: !prev[module] }));
  };

  // Shared link styles
  const getLinkStyle = (isActive) => ({
    transition: "all 0.3s ease-in-out",
    fontWeight: "500",
  });

  const handleMouseEnter = (e, isActive) => {
    if (!isActive) {
      e.currentTarget.style.backgroundColor = "rgba(238, 82, 60, 0.1)";
      e.currentTarget.style.borderColor = "var(--bs-primary)";
      e.currentTarget.style.transform = "translateX(5px)";
      e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.1)";
    }
  };

  const handleMouseLeave = (e, isActive) => {
    if (!isActive) {
      e.currentTarget.style.backgroundColor = "white";
      e.currentTarget.style.borderColor = "var(--bs-gray-300)";
      e.currentTarget.style.transform = "translateX(0)";
      e.currentTarget.style.boxShadow = "0 1px 6px rgba(62, 96, 147, 0.18)";
    }
  };

  const renderLeafItem = (item) => {
    const isActive = moduleName === item.module;
    return (
      <div className="nav-item" key={item.path}>
        <Link
          to={item.path}
          className={`nav-link d-flex align-items-center gap-3 px-3 py-3 rounded-10 text-decoration-none transition-all ${
            isActive
              ? "bg-primary text-white shadow-sm"
              : "text-dark bg-white border border-gray-300 shadow-sm"
          }`}
          aria-current={isActive ? "page" : undefined}
          style={getLinkStyle(isActive)}
          onMouseEnter={(e) => handleMouseEnter(e, isActive)}
          onMouseLeave={(e) => handleMouseLeave(e, isActive)}
        >
          <div
            className={`d-flex align-items-center justify-content-center rounded-8 ${
              isActive ? "bg-white bg-opacity-20" : "bg-primary"
            }`}
            style={{ width: "35px", height: "35px" }}
          >
            <span
              className={`material-symbols-outlined fs-md ${
                isActive ? "text-primary" : "text-white"
              }`}
            >
              {item.icon}
            </span>
          </div>
          <span className="flex-fill fw-semibold">{t(item.label)}</span>
          {isActive && (
            <span className="material-symbols-outlined fs-sm text-white">
              chevron_right
            </span>
          )}
        </Link>
      </div>
    );
  };

  const renderAccordionItem = (item) => {
    const isOpen = !!openMenus[item.module];
    const visibleChildren = item.children.filter(
      (child) => child.show !== false
    );
    if (visibleChildren.length === 0) return null;

    const hasActiveChild =
      item.module === moduleName ||
      visibleChildren.some((child) => isChildActive(child, item.module));

    return (
      <div className="nav-item" key={item.module}>
        {/* Accordion Header */}
        <button
          type="button"
          onClick={() => toggleMenu(item.module)}
          className={`nav-link w-100 d-flex align-items-center gap-3 px-3 py-3 rounded-10 text-decoration-none border-0 ${
            hasActiveChild
              ? "bg-primary text-white shadow-sm"
              : "text-dark bg-white border border-gray-300 shadow-sm"
          }`}
          style={{
            transition: "all 0.3s ease-in-out",
            fontWeight: "500",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            if (!hasActiveChild) {
              e.currentTarget.style.backgroundColor = "rgba(238, 82, 60, 0.1)";
              e.currentTarget.style.borderColor = "var(--bs-primary)";
              e.currentTarget.style.transform = "translateX(5px)";
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(0, 0, 0, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            if (!hasActiveChild) {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "var(--bs-gray-300)";
              e.currentTarget.style.transform = "translateX(0)";
              e.currentTarget.style.boxShadow =
                "0 1px 6px rgba(62, 96, 147, 0.18)";
            }
          }}
        >
          <div
            className={`d-flex align-items-center justify-content-center rounded-8 ${
              hasActiveChild ? "bg-white bg-opacity-20" : "bg-primary"
            }`}
            style={{ width: "35px", height: "35px", flexShrink: 0 }}
          >
            <span
              className={`material-symbols-outlined fs-md ${
                hasActiveChild ? "text-primary" : "text-white"
              }`}
            >
              {item.icon}
            </span>
          </div>
          <span className="flex-fill fw-semibold text-start">
            {t(item.label)}
          </span>
          <span
            className={`material-symbols-outlined fs-sm ${
              hasActiveChild ? "text-white" : "text-muted"
            }`}
            style={{
              transition: "transform 0.3s ease-in-out",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            expand_more
          </span>
        </button>

        {/* Accordion Children */}
        <div
          style={{
            overflow: "hidden",
            maxHeight: isOpen ? `${visibleChildren.length * 64}px` : "0px",
            transition: "max-height 0.35s ease-in-out",
          }}
        >
          <div className="d-flex flex-column gap-1 pt-1 ps-3">
            {/* Vertical connector line */}
            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  left: "16px",
                  top: "0",
                  bottom: "0",
                  width: "2px",
                  backgroundColor: "var(--bs-primary)",
                  opacity: 0.2,
                  borderRadius: "2px",
                }}
              />
              <div className="d-flex flex-column gap-1 ps-4">
                {visibleChildren.map((child) => {
                  const active = isChildActive(child, item.module);
                  return (
                    <Link
                      key={child.path}
                      to={child.path}
                      className={`nav-link d-flex align-items-center gap-3 px-3 py-2 rounded-10 text-decoration-none ${
                        active
                          ? "bg-primary text-white shadow-sm"
                          : "text-dark bg-white border border-gray-300 shadow-sm"
                      }`}
                      aria-current={active ? "page" : undefined}
                      style={{
                        transition: "all 0.3s ease-in-out",
                        fontWeight: "500",
                        fontSize: "0.875rem",
                      }}
                      onMouseEnter={(e) => handleMouseEnter(e, active)}
                      onMouseLeave={(e) => handleMouseLeave(e, active)}
                    >
                      <div
                        className={`d-flex align-items-center justify-content-center rounded-8 ${
                          active ? "bg-white bg-opacity-20" : "bg-primary"
                        }`}
                        style={{ width: "28px", height: "28px", flexShrink: 0 }}
                      >
                        <span
                          className={`material-symbols-outlined ${
                            active ? "text-primary" : "text-white"
                          }`}
                          style={{ fontSize: "16px" }}
                        >
                          {child.icon}
                        </span>
                      </div>
                      <span className="flex-fill fw-semibold">
                        {t(child.label)}
                      </span>
                      {active && (
                        <span className="material-symbols-outlined fs-sm text-white">
                          chevron_right
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="offcanvas offcanvas-start bg-white border-0 shadow-lg"
      data-bs-scroll="true"
      data-bs-backdrop="true"
      tabIndex="-1"
      id="offcanvasMainNav"
      aria-labelledby="offcanvasMainNavLabel"
      style={{ width: "320px" }}
    >
      {/* ----- Enhanced Header Section ----- */}
      <div
        className="offcanvas-header flex-column align-items-start p-0 border-bottom border-gray-300"
        style={{
          background:
            "linear-gradient(135deg, rgba(238, 82, 60, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)",
        }}
      >
        {/* Close button with enhanced styling */}
        <div className="w-100 d-flex justify-content-end p-3 pb-0 mb-2">
          <button
            type="button"
            className="btn btn-outline-primary rounded-circle d-flex align-items-center justify-content-center border-2"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            style={{
              width: "40px",
              height: "40px",
              transition: "all 0.3s ease-in-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--bs-primary)";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--bs-primary)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <span className="material-symbols-outlined fs-md">arrow_back</span>
          </button>
        </div>

        {/* Enhanced User Profile Section */}
        <div className="user w-100 p-4 pt-2">
          <div className="d-flex align-items-center gap-3 p-3 bg-white rounded-15 shadow-sm border border-gray-300">
            <div
              className="avatar rounded-circle overflow-hidden border-3 border-primary shadow-sm"
              style={{ width: "60px", height: "60px" }}
            >
              <img
                src={userImage}
                alt="UserImage"
                className="w-100 h-100 object-fit-cover object-center"
              />
            </div>
            <div className="name flex-fill">
              <h6 className="fw-bold mb-1 text-dark ps-1">
                {userInfo.name} {userInfo.surname}
              </h6>
              <p className="fs-sm text-muted mb-1 ps-1">{userInfo.position}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ----- Enhanced Navigation Menu ----- */}
      <div
        className="offcanvas-body p-0"
        style={{ backgroundColor: "var(--bs-gray-200)" }}
      >
        <div className="p-4">
          <h6
            className="text-muted fw-bold mb-3 text-uppercase ls-1"
            style={{ fontSize: "0.75rem", letterSpacing: "1px" }}
          >
            {t("Navigation")}
          </h6>

          <nav className="nav flex-column gap-1">
            {menuItems.map((item) => {
              if (item.show === false) return null;

              // Render accordion if item has children
              if (item.children) {
                return renderAccordionItem(item);
              }

              // Render standard leaf item
              return renderLeafItem(item);
            })}
          </nav>
        </div>

        {/* Footer Section */}
        <div className="mt-auto p-4 border-top border-gray-300 bg-white">
          <div className="text-center">
            <p className="fs-xs text-muted mb-2">
              {t("Powered by")}{" "}
              <span className="fw-bold text-primary">LOBEES</span>
            </p>
            <div className="d-flex justify-content-center gap-2">
              <span className="badge bg-danger-subtle text-primary fs-xs px-2 py-1 rounded-pill">
                v2.0
              </span>
              <span className="badge border border-primary text-primary fs-xs px-2 py-1 rounded-pill">
                {t("Backend Version")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftMenuPanelPopup;
